import { useState } from 'react';
import { useConditionalEffect, usePrevious } from 'react-delta';

import * as K from 'common/constants';
import { useDebouncedValue } from 'hooks/useDebouncedValue';

import Link from 'components/common/Link/Link';
import ClearableInput from 'components/common/ClearableInput/ClearableInput';
import OverlayTrigger from 'components/common/OverlayTrigger/OverlayTrigger';
import DropdownItems from 'components/common/DropdownItems/DropdownItems';

type AlgoliaMatch = {
  content: string;
  url: string;
};

// Algolia connection info. Could move to config at some point.
const ALGOLIA = {
  appId: '5PRFRAX4SB',
  apiKey: '632e51f7b8e364ad743d7ed9c9db4d96',
  indexName: 'docs',
};

const DEBOUNCE_MS = 300;

// cache search results by query
const RESULTS_CACHE: Record<string, AlgoliaMatch[]> = {};

/**
 * A search box that gets results from Algolia, a paid third-party search service that indexes our help docs.
 *
 * Algolia provides UI components in a product called InstantSearch. We get a lot more control over the look
 * and behavior if we use the REST API (https://www.algolia.com/doc/rest-api/search/). One advantage is that we
 * can render matches as internal links, so that clicking one doesn't reload the app.
 */
function HelpSearchBox(): JSX.Element {
  const [helpFilter, setHelpFilter] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [matches, setMatches] = useState<AlgoliaMatch[]>(K.EMPTY_ARRAY);
  const debouncedHelpFilter = useDebouncedValue(helpFilter, DEBOUNCE_MS);

  // If we don't already have results for the given query, call the Algolia API with a REST call over
  // fetch(), and pull out the data we need.
  const fetchResults = (query: string) => {
    const results = RESULTS_CACHE[query];
    if (results) {
      // cache hit
      setMatches(results);
      setShowSearchResults(results.length > 0);
      return;
    }

    const params = new URLSearchParams();
    params.set('x-algolia-application-id', ALGOLIA.appId);
    params.set('x-algolia-api-key', ALGOLIA.apiKey);
    const url = 'https://' + ALGOLIA.appId.toLowerCase() + '-dsn.algolia.net/1/indexes/*/queries?' + params.toString();

    fetch(url, {
      method: 'POST',
      body: JSON.stringify({
        requests: [
          {
            indexName: ALGOLIA.indexName,
            query,
          },
        ],
      }),
    })
      .then((response) => response.json())
      .then((json) => {
        const hits = json?.results?.[0]?.hits || [];
        const newResults = hits.map(({ content, url }: { content: string; url: string }) => ({ content, url }));
        RESULTS_CACHE[query] = newResults;
        setMatches(newResults);
        setShowSearchResults(newResults.length > 0);
      });
  };

  // Send a search request to Algolia when the input changes (unless it's empty).
  useConditionalEffect(() => {
    if (debouncedHelpFilter) {
      fetchResults(debouncedHelpFilter);
    }
  }, debouncedHelpFilter !== usePrevious(debouncedHelpFilter));

  // convert Algolia matches into menu items for DropdownItems
  const section = matches.map((match) => ({
    text: match.content,
    href: match.url,
    clickHandler: () => {
      setShowSearchResults(false);
      setHelpFilter('');
    },
  }));

  const searchResults =
    !helpFilter || matches.length === 0 ? <></> : <DropdownItems sections={[section]} itemAs={Link} />;

  return (
    <OverlayTrigger
      placement="bottom-start"
      overlay={searchResults}
      popoverClass="Dropdown__Menu"
      showOverlay={showSearchResults}
      setShowOverlay={setShowSearchResults}
      className="FilterInput__Wrapper"
      disabled
    >
      <ClearableInput
        placeholder="Search help"
        size="sm"
        value={helpFilter}
        onChange={(text) => setHelpFilter(text)}
        className="HelpNav__Search"
      />
    </OverlayTrigger>
  );
}

export default HelpSearchBox;
